import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import MarcaEntreElas from "@/components/feature/MarcaEntreElas";

// Lida com magic link gerado pelo servidor (admin generateLink), que usa fluxo
// implícito (#access_token no hash) em vez do PKCE (?code=). Sem isso, o SDK em
// modo PKCE não reconhece a sessão e a integrante não consegue entrar.
async function tryImplicitSessionFromHash(): Promise<boolean> {
  const hash = window.location.hash;
  if (!hash || hash.indexOf("access_token") === -1) return false;

  const params = new URLSearchParams(hash.replace(/^#/, ""));
  const accessToken = params.get("access_token");
  const refreshToken = params.get("refresh_token");
  if (!accessToken || !refreshToken) return false;

  try {
    const { data, error } = await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    if (error || !data.user?.email) return false;

    window.history.replaceState(null, "", window.location.pathname);
    return true;
  } catch {
    return false;
  }
}

export default function MyProfile() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [authMode, setAuthMode] = useState<'magic_link' | 'password'>('magic_link');
  const [loading, setLoading] = useState(false);
  const [autoLoading, setAutoLoading] = useState(true);
  const [error, setError] = useState("");
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  // Logica compartilhada de redirecionamento apos autenticacao
  async function attemptAutoRedirect(userEmail: string): Promise<boolean> {
    const { data: member } = await supabase.rpc("my_member_profile");

    if (!member) {
      return false;
    }

    localStorage.setItem("entre-elas-email", userEmail);
    navigate(`/perfil/${member.id}`);
    return true;
  }

  // Evita redirect duplo entre onAuthStateChange e autoAccess
  const redirectingRef = useRef(false);

  // Tenta acesso automático pela sessão do Supabase Auth (ex: voltou do magic link)
  useEffect(() => {
    let cancelled = false;

    // Subscription: captura SIGNED_IN do SDK (inclusive troca automática de PKCE code)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (cancelled || redirectingRef.current) return;

        if (event === 'SIGNED_IN' && session?.user?.email) {
          redirectingRef.current = true;
          const userEmail = session.user.email;
          const redirected = await attemptAutoRedirect(userEmail);

          if (cancelled) return;

          if (!redirected) {
            setError(
              "Seu email foi autenticado, mas não encontramos um cadastro aprovado vinculado a ele. Verifique se seu cadastro já foi aprovado pela administradora."
            );
            redirectingRef.current = false;
            setAutoLoading(false);
          }
        }
      }
    );

    async function autoAccess() {
      const result = await supabase.auth.getSession();
      let session = result.data.session;

      // 1) Magic link do admin (generateLink): fluxo implícito (#access_token)
      if (!session) {
        const implicitHandled = await tryImplicitSessionFromHash();
        if (implicitHandled) {
          const fresh = await supabase.auth.getSession();
          session = fresh.data.session;
        }
      }

      // 2) PKCE: se nao tem sessao, troca o code da URL com exchangeCodeForSession
      if (!session) {
        const search = window.location.search;
        const searchParams = new URLSearchParams(search);
        const code = searchParams.get('code');

        if (code) {
          const { data, error: pkceError } = await supabase.auth.exchangeCodeForSession(code);

          if (cancelled) return;

          if (!pkceError && data.session) {
            // Limpa a URL pra evitar re-troca em refresh
            window.history.replaceState(null, '', window.location.pathname);
            session = data.session;
          } else {
            setAutoLoading(false);
            setError("Link de acesso inválido ou expirado. Solicite um novo link.");
            return;
          }
        }
      }

      if (cancelled) return;

      if (!session?.user?.email) {
        setAutoLoading(false);
        const savedEmail = localStorage.getItem("entre-elas-email");
        if (savedEmail) setEmail(savedEmail);
        return;
      }

      // Se onAuthStateChange já tratou, não duplica
      if (redirectingRef.current) return;

      redirectingRef.current = true;
      const userEmail = session.user.email;

      const redirected = await attemptAutoRedirect(userEmail);

      if (cancelled) return;

      if (!redirected) {
        setError(
          "Seu email foi autenticado, mas não encontramos um cadastro aprovado vinculado a ele. Verifique se seu cadastro já foi aprovado pela administradora."
        );
        redirectingRef.current = false;
        setAutoLoading(false);
      }
    }

    autoAccess();

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [navigate]);

  const handleMagicLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError("");

    // Verifica se o email pertence a um membro aprovado/ativo ANTES de enviar o link
    const { data: memberCheck } = await supabase.rpc("member_email_status", {
      p_email: email.trim(),
    });

    if (!memberCheck?.existe || (memberCheck.status !== "approved" && memberCheck.status !== "active")) {
      setError("Email não encontrado ou cadastro ainda não aprovado. Verifique se seu cadastro já foi aprovado pela administradora ou faça seu cadastro primeiro.");
      setLoading(false);
      return;
    }

    const basePath = __BASE_PATH__.split("/").filter(Boolean).join("/");
    const pathPrefix = basePath ? `/${basePath}` : "";

    const { error: authError } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: {
        emailRedirectTo: `${window.location.origin}${pathPrefix}/meu-perfil`,
      },
    });

    if (authError) {
      setError(authError.message || "Erro ao enviar o link de acesso. Tente novamente.");
      setLoading(false);
      return;
    }

    localStorage.setItem("entre-elas-email", email.trim());
    setMagicLinkSent(true);
    setLoading(false);
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;

    setLoading(true);
    setError("");

    const { data, error: authError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password: password.trim(),
    });

    if (authError) {
      // Mensagem única e verdadeira: o Supabase só retorna invalid_credentials
      // quando email/senha não batem, sem como distinguir "sem senha" de "senha errada".
      setError("E-mail ou senha incorretos. Verifique os dados ou use o Magic Link para entrar.");
      setLoading(false);
      return;
    }

    // Login com senha bem-sucedido — redireciona pro perfil
    const userEmail = data.user?.email || email.trim();
    const redirected = await attemptAutoRedirect(userEmail);

    if (!redirected) {
      setError(
        "Seu email foi autenticado, mas não encontramos um cadastro aprovado vinculado a ele. Verifique se seu cadastro já foi aprovado pela administradora."
      );
    }
    setLoading(false);
  };

  const handleTabChange = (mode: 'magic_link' | 'password') => {
    setAuthMode(mode);
    setError("");
    setPassword("");
  };

  return (
    <div className="min-h-screen bg-beige flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-10">
          <MarcaEntreElas />
          <div className="flex items-center justify-center gap-2 mt-3">
            <span className="inline-block w-6 h-px bg-primary/40" />
            <span className="inline-block w-1 h-1 rounded-full bg-primary" />
            <span className="inline-block w-6 h-px bg-primary/40" />
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl p-8 md:p-10 shadow-sm">
          {autoLoading ? (
            <div className="text-center py-8">
              <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
              <p className="font-body text-sm text-brown">Buscando seu perfil...</p>
            </div>
          ) : magicLinkSent ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-50 mx-auto mb-6">
                <i className="ri-mail-send-line text-2xl text-green-500" />
              </div>
              <h2 className="font-display text-xl md:text-2xl text-brown mb-3">
                Verifique seu email
              </h2>
              <p className="font-body text-sm text-brown/70 leading-relaxed mb-2">
                Enviamos um link de acesso para <strong>{email}</strong>.
              </p>
              <p className="font-body text-xs text-brown/50 leading-relaxed mb-6">
                Clique no link que chegou no seu email para acessar seu perfil. Se não encontrar, verifique a pasta de spam.
              </p>
              <button
                onClick={() => {
                  setMagicLinkSent(false);
                  setError("");
                }}
                className="font-body text-sm text-primary hover:text-primary-dark transition-colors underline cursor-pointer"
              >
                Usar outro email
              </button>
              {/* Logout from magic link session */}
              <button
                onClick={async () => { await supabase.auth.signOut(); navigate("/"); }}
                className="block mx-auto mt-3 font-body text-xs text-brown/40 hover:text-red-500 transition-colors underline cursor-pointer"
              >
                Sair
              </button>
            </div>
          ) : (
            <>
              {/* Tabs — Segmented Control */}
              <div className="flex p-1 mb-6 rounded-full bg-beige">
                <button
                  onClick={() => handleTabChange('magic_link')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-full font-body text-sm transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    authMode === 'magic_link'
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-brown/60 hover:text-brown'
                  }`}
                >
                  <i className="ri-magic-line" />
                  Magic Link
                </button>
                <button
                  onClick={() => handleTabChange('password')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-full font-body text-sm transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    authMode === 'password'
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-brown/60 hover:text-brown'
                  }`}
                >
                  <i className="ri-lock-line" />
                  Entrar com senha
                </button>
              </div>

              {/* Header */}
              <div className="text-center mb-6">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary/10 mx-auto mb-4">
                  {authMode === 'magic_link' ? (
                    <i className="ri-user-line text-2xl text-primary" />
                  ) : (
                    <i className="ri-lock-line text-2xl text-primary" />
                  )}
                </div>
                <h2 className="font-display text-xl md:text-2xl text-brown mb-2">
                  Acessar meu perfil
                </h2>
                <p className="font-body text-sm text-brown/60">
                  {authMode === 'magic_link'
                    ? "Digite o email usado no cadastro para receber um link de acesso."
                    : "Digite seu email e senha para acessar seu perfil."}
                </p>
              </div>

              {error && (
                <div className="mb-5 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 font-body text-sm">
                  {error}
                </div>
              )}

              {/* Magic Link Form */}
              {authMode === 'magic_link' && (
                <form onSubmit={handleMagicLinkSubmit} className="space-y-4">
                  <div>
                    <label className="font-body text-sm text-brown block mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError("");
                      }}
                      placeholder="seu@email.com"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full font-body text-sm px-6 py-3.5 rounded-full bg-primary text-white tracking-wide uppercase transition-all duration-300 hover:bg-primary-dark disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin" />
                        Enviando link...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-mail-send-line" />
                        Receber link de acesso
                      </span>
                    )}
                  </button>
                </form>
              )}

              {/* Password Login Form */}
              {authMode === 'password' && (
                <form onSubmit={handlePasswordLogin} className="space-y-4">
                  <div className="px-4 py-3 rounded-lg bg-beige/70 border border-nude font-body text-xs text-brown/70 leading-relaxed">
                    Para entrar com senha, ela precisa ter sido criada antes na tela do seu perfil. Se você nunca definiu uma senha, use a aba <strong>Magic Link</strong>.
                  </div>
                  <div>
                    <label className="font-body text-sm text-brown block mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError("");
                      }}
                      placeholder="seu@email.com"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                    />
                  </div>
                  <div>
                    <label className="font-body text-sm text-brown block mb-2">
                      Senha
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setError("");
                        }}
                        placeholder="Sua senha"
                        required
                        className="w-full px-4 py-3 pr-10 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-brown/40 hover:text-brown/70 transition-colors cursor-pointer"
                        tabIndex={-1}
                      >
                        <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"} />
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full font-body text-sm px-6 py-3.5 rounded-full bg-primary text-white tracking-wide uppercase transition-all duration-300 hover:bg-primary-dark disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin" />
                        Entrando...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-login-box-line" />
                        Entrar
                      </span>
                    )}
                  </button>

                  <p className="text-center font-body text-xs text-brown/40 mt-2">
                    A senha é opcional. Se preferir, use a aba{" "}
                    <button
                      type="button"
                      onClick={() => handleTabChange('magic_link')}
                      className="text-primary hover:underline cursor-pointer"
                    >
                      Magic Link
                    </button>{" "}
                    para receber um link por email.
                  </p>

                </form>
              )}

              {/* Esqueci minha senha — separado do botão ENTRAR pra evitar clique por engano */}
              {authMode === 'password' && (
                <div className="mt-8 pt-6 border-t border-nude text-center">
                  <Link
                    to="/recuperar-senha"
                    className="font-body text-sm text-brown/60 hover:text-primary transition-colors underline underline-offset-4"
                  >
                    Esqueci minha senha
                  </Link>
                </div>
              )}

              {/* "Ainda não faz parte" — apenas na aba Magic Link */}
              {authMode === 'magic_link' && (
                <div className="mt-6 pt-5 border-t border-nude text-center">
                  <p className="font-body text-xs text-brown/50 mb-3">
                    Ainda não faz parte da comunidade?
                  </p>
                  <Link
                    to="/cadastro"
                    className="inline-block font-body text-sm px-6 py-2.5 rounded-full border border-primary text-primary tracking-wide uppercase transition-all duration-300 hover:bg-primary hover:text-white whitespace-nowrap"
                  >
                    Quero me cadastrar
                  </Link>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center font-body text-xs text-brown/40 mt-8">
          &copy; {new Date().getFullYear()} Entre Elas
        </p>
      </div>
    </div>
  );
}